import React, { FormEvent, useCallback, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useFetch } from 'use-http';
import { push, replace } from 'connected-react-router';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import IndexReferenceType from 'src/models/IndexReferenceType';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import User from 'src/models/User';
import actions from 'src/actions';
import DialogList from 'src/dialogs';
import ContractHoldDocs from '../../LeasingComponents/redesign/ContractHoldDocs';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import PropertyUtil from 'src/util/PropertyUtil';
import WIOwnership from 'src/models/tract_detail/WIOwnership';
import Owner from 'src/models/tract_detail/Owner';
import Ownership from 'src/models/tract_detail/Ownership';
import AddWIOwnershipData from 'src/models/tract_detail/add_ownership_revised/AddWIOwnership';
import ContractWorkingInterestList from '../../LeasingComponents/redesign/ContractWorkingInterestList';
import OwnerUtil from 'src/util/OwnerUtil';
import WIContractOwnership from 'src/models/tract_detail/WIContractOwnership';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';
import BatteryStorage, { BatteryStorageHold } from 'src/models/contract_types/BatteryStorage';
// import MathUtil from 'src/util/MathUtil';
import AddOwnershipPages from '../AddOwnershipPages';
import AgreementUtil from 'src/util/AgreementUtil';
import RHFToggle from 'src/components/controls/tempRHFControls/RHFToggle';
import ProvisionLeaseClause from 'src/models/ProvisionLeaseClause';
import ProvisionClauseType from 'src/models/ProvisionClauseType';
import LandContractType from 'src/models/LandContractType';
import ProvisionClause from 'src/components/common/ProvisionClause';

const BatteryStorageInfo = () => {
    const { register, handleSubmit, reset, errors, formState, getValues, setValue, watch } = useForm();
    const { del, get, put, post, response } = useFetch();
    const { isDirty } = formState;

    const appContext = useContext<IAppContextState>(AppContext);
    const dispatch = useDispatch();
    const dialogContext = React.useContext<dialogCreators>(DialogContext);
    // const watchExpirationCalcFields = watch(['effectiveDate', 'currentHold.primaryTerm', 'currentHold.primaryTermTypeId']);
    // tslint:disable-next-line: max-line-length
    const watchEasementFields = watch(['currentHold.constructionPeriod', 'currentHold.constructionExtensionPeriod', 'currentHold.accessEasement', 'currentHold.transmissionEasement', 'currentHold.temporaryUseAreas', 'currentHold.cropBuyout', 'currentHold.substationArea']);
    const watchPeriodFields = watch([
        'currentHold.developmentPeriod',
        'currentHold.developmentPeriodExtension',
        'currentHold.developmentPeriodSecondExtension',
        'currentHold.operationsPeriod',
        'currentHold.extendedOperationsPeriod',
        'currentHold.restorationPeriod',
    ]);

    const [batteryStorageData, setBatteryStorageData] = useState<BatteryStorage>(undefined);
    const [provisionClauseTypes, setProvisionClauseTypes] = useState<Array<ProvisionClauseType>>(undefined);
    const [attachedProvisions, setAttachedProvisions] = useState<Array<ProvisionLeaseClause>>(undefined);
    const [measurementTypes, setMeasurementTypes] = useState<Array<IndexReferenceType>>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [currentStorageHold, setCurrentStorageHold] = useState<number>(0);
    // const [isPerpetual, setIsPerpetual] = useState<boolean>(false);
    const [selectedWIOwnershipId, setSelectedWIOwnershipId] = useState<number>(0);
    const [royalty, setRoyalty] = useState(0);
    const [copyLeaseholdClicked, setCopyLeaseholdClicked] = useState<boolean>(false);
    const [sourceSelected, setSourceSelected] = useState<boolean>(false);

    useEffect(() => {
        console.log('Battery Storage Page loaded');
        getMeasurementTypes();
        getUsers();
        getProvisionClauseTypes();
    }, []);

    useEffect(() => {
        if (batteryStorageData?.id > 0) {
            reset(prepData(batteryStorageData));
        }
    }, [batteryStorageData, currentStorageHold]);

    useEffect(() => {
        if (appContext.batteryStorageId > 0 && users.length > 0 && measurementTypes.length > 0) {
            getBatteryStorageData(appContext.batteryStorageId);
        }
    }, [users, measurementTypes]);

    useEffect(() => {
        if (batteryStorageData?.id > 0) {
            if (
                appContext.batteryStorageHoldId > 0 &&
                batteryStorageData?.batteryStorageHolds?.findIndex((swh) => swh.id === appContext.batteryStorageHoldId) > -1
            ) {
                const waiverHoldIndex = batteryStorageData?.batteryStorageHolds?.findIndex((swh) => swh.id === appContext.batteryStorageHoldId);
                setCurrentStorageHold(waiverHoldIndex);
            } else if (appContext.batteryStorageHoldId === 0) {
                const tempBatteryStorageData = batteryStorageData;
                tempBatteryStorageData.batteryStorageHolds.push(new BatteryStorageHold());
                setBatteryStorageData(tempBatteryStorageData);
                setCurrentStorageHold(tempBatteryStorageData?.batteryStorageHolds?.length - 1);
                setRoyalty(0);
                setAttachedProvisions([]);
            }
        }
    }, [appContext.batteryStorageHoldId, batteryStorageData, reset]);

    // Enable Copy Leasehold button when the selected Source changes from default
    const handleSourceSelected = (e: FormEvent<HTMLSelectElement>) => {
        const selectedItem = e.target as HTMLSelectElement;

        if (selectedItem.value !== '-- Select --') {
            setSourceSelected(true);
            setCopyLeaseholdClicked(true);
        }
    };                

    const prepData = useCallback(
        (roughBatteryStorageData: BatteryStorage) => {
            console.log('Rough Waiver Data:', roughBatteryStorageData);

            const formData: BatteryStorage = {
                id: roughBatteryStorageData.id,
                dlcLeaseNumber: roughBatteryStorageData.dlcLeaseNumber,
                contractName: roughBatteryStorageData.contractName,
                createdDate: roughBatteryStorageData.createdDate ? (moment(roughBatteryStorageData.createdDate).format('YYYY-MM-DD') as unknown as Date) : null,
                createdByUserId: roughBatteryStorageData.createdByUserId,
                leasedByUserId: roughBatteryStorageData.leasedByUserId,
                leasedBy: roughBatteryStorageData.leasedBy,
                lessorAka: roughBatteryStorageData.lessorAka,
                originalLessee: roughBatteryStorageData.originalLessee,
                lesseeName: roughBatteryStorageData.lesseeName,
                lesseeAddressLine1: roughBatteryStorageData.lesseeAddressLine1,
                lesseeAddressLine2: roughBatteryStorageData.lesseeAddressLine2,
                lesseeCity: roughBatteryStorageData.lesseeCity,
                lesseeState: roughBatteryStorageData.lesseeState,
                lesseeZipCode: roughBatteryStorageData.lesseeZipCode,
                lesseePhoneNumber: roughBatteryStorageData.lesseePhoneNumber,
                isNotOriginalLessee: roughBatteryStorageData.isNotOriginalLessee,
                leaseLessorNotes: roughBatteryStorageData.leaseLessorNotes,
                fedStateLeaseNumber: roughBatteryStorageData.fedStateLeaseNumber,
                legalDescription: roughBatteryStorageData.legalDescription,
                rteLegalDescription: roughBatteryStorageData.rteLegalDescription,
                recordDate: roughBatteryStorageData.recordDate ? (moment(roughBatteryStorageData.recordDate).format('YYYY-MM-DD') as unknown as Date) : null,
                recordTypeId: roughBatteryStorageData.recordTypeId,
                volume: roughBatteryStorageData.volume,
                page: roughBatteryStorageData.page,
                instrumentNumber: roughBatteryStorageData.instrumentNumber,
                isVoid: roughBatteryStorageData.isVoid,
                projectId: roughBatteryStorageData.projectId,
                isFiledByMemo: roughBatteryStorageData.isFiledByMemo,
                inHandDate: roughBatteryStorageData.inHandDate ? (new Date(roughBatteryStorageData.inHandDate).toISOString().split('T')[0] as unknown as Date) : null,
                originalInHandDate: roughBatteryStorageData.originalInHandDate ? (new Date(roughBatteryStorageData.originalInHandDate).toISOString().split('T')[0] as unknown as Date) : null,
                commitDate: roughBatteryStorageData.commitDate ? (new Date(roughBatteryStorageData.commitDate).toISOString().split('T')[0] as unknown as Date) : null,
                voidDate: roughBatteryStorageData.voidDate ? (new Date(roughBatteryStorageData.voidDate).toISOString().split('T')[0] as unknown as Date) : null,
                leaseDate: roughBatteryStorageData.leaseDate ? (new Date(roughBatteryStorageData.leaseDate).toISOString().split('T')[0] as unknown as Date) : null,
                reminderDate: roughBatteryStorageData.reminderDate ? (new Date(roughBatteryStorageData.reminderDate).toISOString().split('T')[0] as unknown as Date) : null,
                effectiveDate: roughBatteryStorageData.effectiveDate
                    ? (new Date(roughBatteryStorageData.effectiveDate).toISOString().split('T')[0] as unknown as Date)
                    : null,
                recordedDate: roughBatteryStorageData.recordedDate ? (new Date(roughBatteryStorageData.recordedDate).toISOString().split('T')[0] as unknown as Date) : null,
                originalGrossAcres: roughBatteryStorageData.originalGrossAcres,
                clientDocNumber: roughBatteryStorageData.clientDocNumber,
                reminderNote: roughBatteryStorageData.reminderNote,
                ownersOnDocument: roughBatteryStorageData.ownersOnDocument,
                ownersAddressOnDocument: roughBatteryStorageData.ownersAddressOnDocument,
                ownersSSNTaxIdOnDocument: roughBatteryStorageData.ownersSSNTaxIdOnDocument,

                currentHold: {
                    ...roughBatteryStorageData.batteryStorageHolds?.[currentStorageHold],
                    isActive: roughBatteryStorageData.batteryStorageHolds?.[currentStorageHold]?.isActive,
                },
            };
            if (formData.currentHold.provisionLeaseClauses?.length > 0) {
                for (let i = 0; i < formData.currentHold.provisionLeaseClauses.length; i++) {
                    formData.currentHold.provisionLeaseClauses[i].obligationDate = formData.currentHold.provisionLeaseClauses[i].obligationDate
                        ? (moment(formData.currentHold.provisionLeaseClauses[i].obligationDate).format('YYYY-MM-DD') as unknown as Date)
                        : null;
                    formData.currentHold.provisionLeaseClauses[i].obligationEndDate = formData.currentHold.provisionLeaseClauses[i].obligationEndDate
                        ? (moment(formData.currentHold.provisionLeaseClauses[i].obligationEndDate).format('YYYY-MM-DD') as unknown as Date)
                        : null;
                }
                setAttachedProvisions(formData.currentHold.provisionLeaseClauses);
            }

            return formData;
        },
        [currentStorageHold]
    );

    const getBatteryStorageData = async (batteryStorageId: number) => {
        const result: BatteryStorage = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/batteryStorage/${batteryStorageId}`
        );
        if (response.ok) {
            console.log('Waiver Result:', result);
            setBatteryStorageData(finalizeBatteryStorage(result));
        } else {
            window.alert('Get Battery Storage Data failed');
        }
    };

    const getMeasurementTypes = async () => {
        const result = await get(`/v1.0/referenceTypes`);

        if (response.ok) {
            setMeasurementTypes(result);
        }
    };

    const getProvisionClauseTypes = async () => {
        const result: Array<ProvisionClauseType> = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/provisionLeaseClause/types/${LandContractType.BatteryStorage}`
        );
        if (response.ok && Array.isArray(result)) {
            setProvisionClauseTypes(result ?? []);
        }
    };

    const updateBatteryStorage = async (data: BatteryStorage) => {
        await put(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/batteryStorage`,
            data
        );

        if (response.ok) {
            dispatch(
                push(window.location.pathname)
            );

            getBatteryStorageData(appContext.batteryStorageId);
        } else {
            window.alert('Update Battery Storage failed');
        }
    };

    const deleteBatteryStorageHold = (deleteParent: boolean) => {
        if (batteryStorageData?.isGeneratedLease === true && batteryStorageData?.isInHand === true) {
            window.alert('Cannot delete a generated Agreement. Please review and update as necessary in the Agreements tab.');
            return;
        }
        const confirmAction = async () => {
            const deleteId = batteryStorageData?.batteryStorageHolds?.[currentStorageHold].id;
            let currentPath = window.location.pathname.split('/');
            let spliceLength = deleteParent === true ? 5 : 1;
            currentPath.splice(currentPath.length - spliceLength);
            
            // tslint:disable-next-line: max-line-length
            await del(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/batteryStorage/${deleteId}/${deleteParent}`);
            if (response.ok) {
                if (!deleteParent) {
                    const switchToStorageholdId = batteryStorageData?.batteryStorageHolds.find((x) => x.id !== batteryStorageData?.batteryStorageHolds?.[0]?.id)?.id;
                    getBatteryStorageData(appContext.batteryStorageId);
                    dispatch(
                        replace(
                            `${currentPath.join('/')}/${switchToStorageholdId}`
                        )
                    );
                } else {
                    dispatch(
                        replace(
                            `${currentPath.join('/')}`
                        )
                    );
                }
            
            } else {
                window.alert('delete battery storagehold failed');
            }
        };

        let warning = deleteParent
            ? 'This is the only leasehold. The lease will also be deleted. Continue?'
            : 'Are you sure you want to delete this Leasehold?';

        const body = (
            <>
                <div style={{ fontWeight: 'bold' }}>{warning}</div>
            </>
        );

        dialogContext.common.confirmAction(`Delete Leasehold`, body, confirmAction);
    };

    const createBatteryStorage = async (data: BatteryStorage) => {
        let currentPath = window.location.pathname.split('/');
        currentPath.splice(currentPath.length - 3, 3);

        let result = await post(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/batteryStorage`,
            data
        );
        if (response.ok) {
            dispatch(
                replace(
                    `${currentPath.join('/')}/${result.contractId}/battery-storage-holds/${result.contractHoldId}`
                )
            );
            getBatteryStorageData(result.contractId);
        } else {
            window.alert('Create Battery Storage failed');
        }
    };

    const onCopyLeasehold = async () => {
        let copyLeaseholdId = getValues('sourceLeaseholdId');

        // tslint:disable-next-line: max-line-length
        const result = await put(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/batteryStorage/battery-storage-hold/${appContext.batteryStorageHoldId}/copy-leasehold/${copyLeaseholdId}`);

        if (response.ok) {
            let currentPath = window.location.pathname.split('/');
            currentPath.splice(currentPath.length - 1, 1);
    
            dispatch(
                push(`${currentPath.join('/')}/${result}`)
            );
            getBatteryStorageData(appContext.batteryStorageId);
        } else {
            window.alert('call failed');
        }
    };

    const leaseholdList = () => {
        if (batteryStorageData?.batteryStorageHolds?.length > 1) {
            let holds = [...batteryStorageData.batteryStorageHolds];
            holds.pop();
            let leaseholdIds = holds.map((lh: BatteryStorageHold) => {
                return { value: lh.id || 0, label: lh.batteryStorageHoldNumber?.toString() || 'Error' };
            });

            return leaseholdIds;
        } else {
            return [];
        }
    };

    const onSubmit = (data: BatteryStorage) => {
        data.batteryStorageHolds = new Array<BatteryStorageHold>(data.currentHold);
        data = finalizeBatteryStorage(data);
        // data.id = leaseData.id;
        if (data.id > 0) {
            updateBatteryStorage(data);
        } else {
            data.isInHand = true;
            data.inHandDate = new Date();
            data.originalInHandDate = new Date();
            data.isCommitted = true;
            data.commitDate = new Date();
            createBatteryStorage(data);
        }
    };

    const finalizeBatteryStorage = (data: BatteryStorage) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.rteLegalDescription = batteryStorageData?.rteLegalDescription ? batteryStorageData.rteLegalDescription : '';
        data.dlcLeaseNumber = batteryStorageData?.dlcLeaseNumber ? batteryStorageData?.dlcLeaseNumber : 0;

        data.createdByUserId = data.createdByUserId ? parseInt(data.createdByUserId + '', 10) : null;
        data.leasedByUserId = data.leasedByUserId ? parseInt(data.leasedByUserId + '', 10) : null;
        data.recordTypeId = data.recordTypeId ? parseInt(data.recordTypeId + '', 10) : null;
        
        data.originalGrossAcres = data.originalGrossAcres ? parseFloat(data.originalGrossAcres + '') : 0;
        
        data.contractName = data.contractName ? data.contractName : '';
        data.lessorAka = data.lessorAka ? data.lessorAka : '';
        data.leaseLessorNotes = data.leaseLessorNotes ? data.leaseLessorNotes : '';
        data.fedStateLeaseNumber = data.fedStateLeaseNumber ? data.fedStateLeaseNumber : '';
        data.legalDescription = data.legalDescription ? data.legalDescription : '';
        data.volume = data.volume ? data.volume : '';
        data.page = data.page ? data.page : '';
        data.instrumentNumber = data.instrumentNumber ? data.instrumentNumber : '';
        data.ownersOnDocument = data.ownersOnDocument ? data.ownersOnDocument : '';
        data.lesseeName = data.lesseeName ? data.lesseeName : '';
        
        data.isFiledByMemo = data.isFiledByMemo ? data.isFiledByMemo : false;
        data.leaseDate = data.leaseDate ? (moment(data.leaseDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.effectiveDate = data.effectiveDate ? (moment(data.effectiveDate).format('YYYY-MM-DD') as unknown as Date) : null;
        data.recordedDate = data.recordedDate ? (moment(data.recordedDate).format('YYYY-MM-DD') as unknown as Date) : null;
        
        if (!!batteryStorageData) {
            data.reminderNote = batteryStorageData?.reminderNote ? batteryStorageData.reminderNote : '';
            data.lesseeAddressLine1 = batteryStorageData?.lesseeAddressLine1 ? batteryStorageData.lesseeAddressLine1 : '';
            data.lesseeAddressLine2 = batteryStorageData?.lesseeAddressLine2 ? batteryStorageData.lesseeAddressLine2 : '';
            data.lesseeCity  = batteryStorageData?.lesseeCity ? batteryStorageData.lesseeCity : '';
            data.lesseeState = batteryStorageData?.lesseeState ? batteryStorageData.lesseeState : '';
            data.lesseeZipCode = batteryStorageData?.lesseeZipCode ? batteryStorageData.lesseeZipCode : '';
            data.ownersAddressOnDocument = batteryStorageData?.ownersAddressOnDocument ? batteryStorageData.ownersAddressOnDocument : '';
            
            data.isGeneratedLease = batteryStorageData?.isGeneratedLease ? batteryStorageData.isGeneratedLease : false;
            data.isVoid = batteryStorageData?.isVoid ? batteryStorageData.isVoid : false;
            data.isNotOriginalLessee = batteryStorageData?.isNotOriginalLessee ? batteryStorageData.isNotOriginalLessee : false;
            data.isActive = data.currentHold?.isActive ? true : false;
    
            data.voidDate = batteryStorageData?.voidDate ? (moment(batteryStorageData.voidDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.commitDate = batteryStorageData?.commitDate ? (moment(batteryStorageData.commitDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.inHandDate = batteryStorageData?.inHandDate ? (moment(batteryStorageData.inHandDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.originalInHandDate = batteryStorageData?.originalInHandDate ? (moment(batteryStorageData.originalInHandDate).format('YYYY-MM-DD') as unknown as Date) : null;
    
            data.createdDate = batteryStorageData?.createdDate
                ? (moment(batteryStorageData.createdDate).format('YYYY-MM-DD') as unknown as Date)
                : (moment().format('YYYY-MM-DD') as unknown as Date);
            data.reminderDate = batteryStorageData?.reminderDate ? (moment(batteryStorageData.reminderDate).format('YYYY-MM-DD') as unknown as Date) : null;  
        }

        if (data.batteryStorageHolds?.length > 0) {
            let formatted = new Array<BatteryStorageHold>();
            data.batteryStorageHolds.forEach((batteryStorageHold: BatteryStorageHold) => {
                if (formatted.length === currentStorageHold && !!data.currentHold) {
                    formatted.push(finalizeBatteryStorageHold(data.currentHold));
                } else {
                    formatted.push(finalizeBatteryStorageHold(batteryStorageHold));
                }
            });
            data.batteryStorageHolds = [...formatted];
        }

        console.log('Finalized Data:', data);

        return data;
    };

    const finalizeBatteryStorageHold = (data: BatteryStorageHold) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.batteryStorageId = batteryStorageData?.id ? batteryStorageData?.id : 0;

        data.executionBonus = data.executionBonus ? parseFloat(data.executionBonus + '') : null;
        data.constructionFee = data.constructionFee ? parseFloat(data.constructionFee + '') : null;
        data.constructionExtensionFee = data.constructionExtensionFee ? parseFloat(data.constructionExtensionFee + '') : null;
        data.accessFee = data.accessFee ? parseFloat(data.accessFee + '') : null;
        data.accessWidth = data.accessWidth ? parseFloat(data.accessWidth + '') : null;
        data.transmissionFee = data.transmissionFee ? parseFloat(data.transmissionFee + '') : null;
        data.transmissionWidth = data.transmissionWidth ? parseFloat(data.transmissionWidth + '') : null;
        data.temporaryUseFee = data.temporaryUseFee ? parseFloat(data.temporaryUseFee + '') : null;
        data.developmentRentAmount = data.developmentRentAmount ? parseFloat(data.developmentRentAmount + '') : null;
        data.firstExtensionOptionRentAmount = data.firstExtensionOptionRentAmount ? parseFloat(data.firstExtensionOptionRentAmount + '') : null;
        data.secondExtensionOptionRentAmount = data.secondExtensionOptionRentAmount ? parseFloat(data.secondExtensionOptionRentAmount + '') : null;
        data.operationsBaseRentAmount = data.operationsBaseRentAmount ? parseFloat(data.operationsBaseRentAmount + '') : null;
        data.annualEscalationRate = data.annualEscalationRate ? parseFloat(data.annualEscalationRate + '') : 0;
        data.extendedOperationsRentAmount = data.extendedOperationsRentAmount ? parseFloat(data.extendedOperationsRentAmount + '') : null;
        data.restorationTermRentAmount = data.restorationTermRentAmount ? parseFloat(data.restorationTermRentAmount + '') : null;
        data.overdueRateTerm = data.overdueRateTerm ? parseFloat(data.overdueRateTerm + '') : null;
        data.additionalPayments = data.additionalPayments ? parseFloat(data.additionalPayments + '') : null;
        data.cropBuyoutFee = data.cropBuyoutFee ? parseFloat(data.cropBuyoutFee + '') : null;
        data.substationFee = data.substationFee ? parseFloat(data.substationFee + '') : 0;

        data.batteryStorageHoldNumber = data.batteryStorageHoldNumber ? parseInt(data.batteryStorageHoldNumber + '', 10) : null;
        data.executionBonusTypeId = data.executionBonusTypeId ? parseInt(data.executionBonusTypeId + '', 10) : null;
        data.constructionFeeTypeId = data.constructionFeeTypeId ? parseInt(data.constructionFeeTypeId + '', 10) : null;
        data.constructionExtensionFeeTypeId = data.constructionExtensionFeeTypeId ? parseInt(data.constructionExtensionFeeTypeId + '', 10) : null;
        data.accessFeeTypeId = data.accessFeeTypeId ? parseInt(data.accessFeeTypeId + '', 10) : null;
        data.accessWidthTypeId = data.accessWidthTypeId ? parseInt(data.accessWidthTypeId + '', 10) : null;
        data.transmissionFeeTypeId = data.transmissionFeeTypeId ? parseInt(data.transmissionFeeTypeId + '', 10) : null;
        data.transmissionWidthTypeId = data.transmissionWidthTypeId ? parseInt(data.transmissionWidthTypeId + '', 10) : null;
        data.temporaryUseFeeTypeId = data.temporaryUseFeeTypeId ? parseInt(data.temporaryUseFeeTypeId + '', 10) : null;
        data.developmentPeriodTerm = data.developmentPeriodTerm ? parseInt(data.developmentPeriodTerm + '', 10) : null;
        data.developmentPeriodTermTypeId = data.developmentPeriodTermTypeId ? parseInt(data.developmentPeriodTermTypeId + '', 10) : null;
        data.developmentRentTermTypeId = data.developmentRentTermTypeId ? parseInt(data.developmentRentTermTypeId + '', 10) : null;
        data.developmentPeriodExtensionTerm = data.developmentPeriodExtensionTerm ? parseInt(data.developmentPeriodExtensionTerm + '', 10) : null;
        data.developmentPeriodExtensionTermTypeId = data.developmentPeriodExtensionTermTypeId
            ? parseInt(data.developmentPeriodExtensionTermTypeId + '', 10)
            : null;
        data.firstExtensionOptionRentTermTypeId = data.firstExtensionOptionRentTermTypeId
            ? parseInt(data.firstExtensionOptionRentTermTypeId + '', 10)
            : null;
        data.developmentPeriodSecondExtensionTerm = data.developmentPeriodSecondExtensionTerm
            ? parseInt(data.developmentPeriodSecondExtensionTerm + '', 10)
            : null;
        data.developmentPeriodSecondExtensionTermTypeId = data.developmentPeriodSecondExtensionTermTypeId
            ? parseInt(data.developmentPeriodSecondExtensionTermTypeId + '', 10)
            : null;
        data.secondExtensionOptionRentTermTypeId = data.secondExtensionOptionRentTermTypeId
            ? parseInt(data.secondExtensionOptionRentTermTypeId + '', 10)
            : null;
        data.operationsPeriodTerm = data.operationsPeriodTerm ? parseInt(data.operationsPeriodTerm + '', 10) : null;
        data.operationsPeriodTermTypeId = data.operationsPeriodTermTypeId ? parseInt(data.operationsPeriodTermTypeId + '', 10) : null;
        data.operationsBaseRentTermTypeId = data.operationsBaseRentTermTypeId ? parseInt(data.operationsBaseRentTermTypeId + '', 10) : null;
        data.extendedOperationsPeriodTerm = data.extendedOperationsPeriodTerm ? parseInt(data.extendedOperationsPeriodTerm + '', 10) : null;
        data.extendedOperationsPeriodTermTypeId = data.extendedOperationsPeriodTermTypeId
            ? parseInt(data.extendedOperationsPeriodTermTypeId + '', 10)
            : null;
        data.extendedOperationsRentTermTypeId = data.extendedOperationsRentTermTypeId
            ? parseInt(data.extendedOperationsRentTermTypeId + '', 10)
            : null;
        data.restorationTerm = data.restorationTerm ? parseInt(data.restorationTerm + '', 10) : null;
        data.restorationTermTypeId = data.restorationTermTypeId ? parseInt(data.restorationTermTypeId + '', 10) : null;
        data.restorationTermRentTypeId = data.restorationTermRentTypeId ? parseInt(data.restorationTermRentTypeId + '', 10) : null;
        data.cropBuyoutFeeTypeId = data.cropBuyoutFeeTypeId ? parseInt(data.cropBuyoutFeeTypeId + '', 10) : null;
        data.substationFeeTypeId = data.substationFeeTypeId ? parseInt(data.substationFeeTypeId + '', 10) : null;

        data.accessEasement = data.accessEasement ? data.accessEasement : null;
        data.transmissionEasement = data.transmissionEasement ? data.transmissionEasement : null;
        data.temporaryUseAreas = data.temporaryUseAreas ? data.temporaryUseAreas : null;
        data.cropBuyout = data.cropBuyout ? data.cropBuyout : null;
        data.developmentPeriod = data.developmentPeriod ? data.developmentPeriod : null;
        data.developmentPeriodExtension = data.developmentPeriodExtension ? data.developmentPeriodExtension : null;
        data.developmentPeriodSecondExtension = data.developmentPeriodSecondExtension ? data.developmentPeriodSecondExtension : null;
        data.operationsPeriod = data.operationsPeriod ? data.operationsPeriod : null;
        data.escalationAbovePrime = data.escalationAbovePrime ? data.escalationAbovePrime : null;
        data.additionalExtensionOptions = data.additionalExtensionOptions ? data.additionalExtensionOptions : null;
        data.overdueAbovePrime = data.overdueAbovePrime ? data.overdueAbovePrime : null;
        data.extendedOperationsPeriod = data.extendedOperationsPeriod ? data.extendedOperationsPeriod : null;
        data.restorationPeriod = data.restorationPeriod ? data.restorationPeriod : null;
        data.rightsToAssign = data.rightsToAssign ? data.rightsToAssign : null;
        data.isActive = data.isActive ? data.isActive : null;
        data.isHistorical = data.isHistorical ? data.isHistorical : null;
        
        data.hbp = AgreementUtil.parseBool(data.hbp + '');
        data.hbu = AgreementUtil.parseBool(data.hbu + '');

        data.accessDetails = data.accessDetails ? data.accessDetails : '';
        data.transmissionDetails = data.transmissionDetails ? data.transmissionDetails : '';
        data.temporaryUseDetails = data.temporaryUseDetails ? data.temporaryUseDetails : '';
        data.cropBuyoutDetails = data.cropBuyoutDetails ? data.cropBuyoutDetails : '';

        if (data.contractOwnerships?.length > 0) {
            let formatted = new Array<WIContractOwnership>();
            data.contractOwnerships.forEach((owner: WIContractOwnership) => {
                formatted.push(finalizeWIOwnership(owner));
            });
            data.contractOwnerships = [...formatted];
        }

        if (data.provisionLeaseClauses?.length > 0) {
            data.provisionLeaseClauses = data.provisionLeaseClauses.map((plc) => {
                plc.id = plc.id ? parseInt(plc.id + '', 10) : 0;
                plc.contractLookupId = batteryStorageData?.batteryStorageHolds?.[currentStorageHold]?.contractLookupId ?? null;
                plc.invoiceableLookupId = plc.invoiceableLookupId ? parseInt(plc.invoiceableLookupId + '', 10) : null;
                plc.contractAdHocClauseId = plc.contractAdHocClauseId ? parseInt(plc.contractAdHocClauseId + '', 10) : null;
                plc.leaseAdHocClauseId = plc.leaseAdHocClauseId ? parseInt(plc.leaseAdHocClauseId + '', 10) : null;
                plc.contractAddendumClauseId = plc.contractAddendumClauseId ? parseInt(plc.contractAddendumClauseId + '', 10) : null;
                plc.clauseTypeId = plc.clauseTypeId ? parseInt(plc.clauseTypeId + '', 10) : null;
                plc.frequency = plc.frequency ? parseInt(plc.frequency + '', 10) : null;
                plc.obligationDate = plc.obligationDate ? (moment(plc.obligationDate).format('YYYY-MM-DD') as unknown as Date) : null;
                plc.obligationEndDate = plc.obligationEndDate ? (moment(plc.obligationEndDate).format('YYYY-MM-DD') as unknown as Date) : null;
                plc.useClientApproved = plc.useClientApproved ? AgreementUtil.parseBool(plc.useClientApproved + '') : null;
                return plc;
            });
        } else {
            data.provisionLeaseClauses = data.provisionLeaseClauses ?? [];
        }

        return data;
    };

    const finalizeWIOwnership = (data: WIContractOwnership) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.projectId = data.projectId ? parseInt(data.projectId + '', 10) : 0;
        data.ownerLookUpId = data.ownerLookUpId ? parseInt(data.ownerLookUpId + '', 10) : 0;
        data.parentOwnershipId = data.parentOwnershipId ? parseInt(data.parentOwnershipId + '', 10) : 0;
        data.ownershipId = data.ownershipId ? parseInt(data.ownershipId + '', 10) : 0;
        data.contractLookupId = data.contractLookupId ? parseInt(data.contractLookupId + '', 10) : 0;

        data.decimalInterest = data.decimalInterest ? parseFloat(data.decimalInterest + '') : 0;

        data.note = data.note ? data.note : '';

        if (data.orriOwnerships?.length > 0) {
            let formatted = new Array<WIContractOwnership>();
            data.orriOwnerships.forEach((orri: WIContractOwnership) => {
                formatted.push(finalizeWIOwnership(orri));
            });
            data.orriOwnerships = [...formatted];
        }

        return data;
    };

    const getOptions = (type: string, item: string = '') => {
        if (measurementTypes?.length > 0) {
            return measurementTypes
                .filter((mt) => mt.description === type)
                .map((option) => {
                    if (type === 'Fee' && option.name === 'Per Item') {
                        return { value: option.id, label: `Per ${item}` };
                    }
                    return { value: option.id, label: option.name };
                });
        }
        return [];
    };

    const getUsers = async () => {
        const result = await get('v1.0/users/user-list');

        if (response.ok) {
            setUsers(result);
        }
    };

    const previousContractHold = () => {
        if (currentStorageHold > 0) {
            const batteryStorageHoldId = batteryStorageData?.batteryStorageHolds?.[currentStorageHold - 1]?.id;
            setCurrentStorageHold(currentStorageHold - 1);
            dispatch(
                push(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.fieldNumber}/surface-tracts/${appContext.surfaceTractNumber}/ownership/${appContext.ownershipId}/battery-storage/${appContext.batteryStorageId}/battery-storage-holds/${batteryStorageHoldId}`
                )
            );
        }
    };

    const nextContractHold = () => {
        if (currentStorageHold + 1 < batteryStorageData?.batteryStorageHolds?.length) {
            const batteryStorageHoldId = batteryStorageData?.batteryStorageHolds?.[currentStorageHold + 1]?.id;

            setCurrentStorageHold(currentStorageHold + 1);
            dispatch(
                push(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.fieldNumber}/surface-tracts/${appContext.surfaceTractNumber}/ownership/${appContext.ownershipId}/battery-storage/${appContext.batteryStorageId}/battery-storage-holds/${batteryStorageHoldId}`
                )
            );
        }
    };

    // const handleExpirationChange = () => {
    //     calcExpirationDate();
    // };

    const onUpdateWINote = async (ownership: Ownership) => {
        await put(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/surfaceTracts/notes`,
            ownership
        );

        if (response.ok) {
            dispatch(actions.layout.hideDialog());
            getBatteryStorageData(appContext.batteryStorageId);
        } else {
            window.alert(`update wi note failed. note: ${ownership.note}`);
        }
    };

    // const handleTotalBonusPaid = () => {
    //     let rep = getValues();
    //     let bonusPaidTypeId = parseInt(rep.currentHold.bonusPaidTypeId, 10);
    //     let bonusPaid = !isNaN(rep.currentHold.bonusPaid) ? rep.currentHold.bonusPaid : 0;
    //     let total = calculatePrice(bonusPaidTypeId, bonusPaid, rep.grossAcres);

    //     setValue('currentHold.totalBonusPaid', total);
    // };

    // const calculatePrice = (bonusPaidTypeId: number, bonusPaid: number, grossAcres: number) => {
    //     let bonusPaidType = measurementTypes.find((mt) => mt.id === bonusPaidTypeId)?.name;

    //     return bonusPaidType !== undefined ? (bonusPaidType === 'Per Item' ? bonusPaid * grossAcres : bonusPaid) : 0;
    // };

    // const calcExpirationDate = () => {
    //     const term = watchExpirationCalcFields['currentHold.primaryTerm']
    //         ? parseInt(watchExpirationCalcFields['currentHold.primaryTerm'], 10)
    //         : undefined;
    //     const termUnit = watchExpirationCalcFields['currentHold.primaryTermTypeId']
    //         ? watchExpirationCalcFields['currentHold.primaryTermTypeId']
    //         : undefined;
    //     const effectiveDate = watchExpirationCalcFields.effectiveDate
    //         ? new Date(watchExpirationCalcFields.effectiveDate.toString()) || undefined
    //         : undefined;

    //     if (term && termUnit && effectiveDate && !isNaN(term) && termUnit && effectiveDate instanceof Date) {
    //         let termString = measurementTypes.find((mt) => mt.id === parseInt(termUnit, 10))?.name;
    //         switch (termString) {
    //             case 'Years':
    //                 setValue(
    //                     'currentHold.expirationDate',
    //                     new Date(effectiveDate.setFullYear(effectiveDate.getFullYear() + term)).toISOString().split('T')[0]
    //                 );
    //                 break;

    //             case 'Months':
    //                 setValue(
    //                     'currentHold.expirationDate',
    //                     new Date(effectiveDate.setMonth(effectiveDate.getMonth() + term)).toISOString().split('T')[0]
    //                 );
    //                 break;

    //             case 'Days':
    //                 setValue(
    //                     'currentHold.expirationDate',
    //                     new Date(effectiveDate.setDate(effectiveDate.getDate() + term)).toISOString().split('T')[0]
    //                 );
    //                 break;

    //             default:
    //                 break;
    //         }
    //     } else {
    //         setValue('currentHold.expirationDate', '');
    //     }
    // };

    const onTryAddWI = () => {
        if (!isNaN(batteryStorageData?.batteryStorageHolds?.[currentStorageHold]?.id)) {
            dialogContext.contract.addContractOwnership(
                DialogList.ADD_WI_OWNERSHIP,
                'Add Working Interest Ownership',
                undefined,
                undefined,
                appContext.clientNumber,
                appContext.projectNumber,
                AddOwnershipPages.AddOwnershipLandingPage,
                (path: string) => dispatch(push(path)),
                null,
                onSaveAddOwnership
            );
        } else {
            dispatch(actions.layout.showDialog(DialogList.LEASEHOLD_SAVE_BEFORE_ADDING_WI));
        }
    };

    const onAddORRI = (newParentOwnershipId: number) => {
        dialogContext.contract.addContractOwnership(
            DialogList.ADD_ORRI_OWNERSHIP,
            'Add Overriding Royalty Interest Ownership',
            undefined,
            newParentOwnershipId,
            appContext.clientNumber,
            appContext.projectNumber,
            AddOwnershipPages.AddOwnershipLandingPage,
            (path: string) => dispatch(push(path)),
            null,
            onSaveAddOwnership
        );
    };

    const onCopyORRI = (ownershipId: number) => {
        dialogContext.leasehold.cloneORRI(ownershipId, ownerNameForWorkingInterest(ownershipId), () => location.reload());
    };

    const ownerNameForWorkingInterest = (workingInterestId: number) => {
        const wiOwners = PropertyUtil.SafeGet(() => batteryStorageData?.batteryStorageHolds?.[currentStorageHold]?.wiOwners);
        const wiOwnerships = PropertyUtil.SafeGet(() => batteryStorageData?.batteryStorageHolds?.[currentStorageHold]?.contractOwnerships);
        const ownership = wiOwnerships?.filter((wio: Ownership) => wio.id === workingInterestId)[0];
        const owner = wiOwners.filter((o: Owner) => o.ownerLookUpId === ownership.ownerLookUpId)[0];

        return owner?.name || '[Error]';
    };

    const onDeleteORRI = (ownershipId: number) => {
        const wiOwners = PropertyUtil.SafeGet(() => batteryStorageData.batteryStorageHolds?.[currentStorageHold]?.wiOwners);
        const wiOwnerships = PropertyUtil.SafeGet(() => batteryStorageData.batteryStorageHolds?.[currentStorageHold]?.contractOwnerships);
        const ownership: WIOwnership = wiOwnerships?.filter((wio: WIOwnership) => wio.id === ownershipId)[0];

        dialogContext.leasehold.deleteORRI(ownershipId, ownerNameForWorkingInterest(ownershipId), ownership.orriOwnerships, wiOwners, () =>
            location.reload()
        );
    };

    const onDeleteContractOwnership = async (contractOwnershipId: number) => {
        await del(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/contract-ownership/${contractOwnershipId}`
        );
        if (response.ok) {
            dispatch(actions.layout.hideDialog());
            getBatteryStorageData(appContext?.batteryStorageId);
        } else {
            window.alert('Get Surface Waiver Data failed');
        }
    };

    const openOwnershipNotes = (ownershipId: number) => {
        // this.setState({ ownershipNotesId: ownershipId });
        setSelectedWIOwnershipId(ownershipId);
        dialogContext.tract.openOwnershipNotes(
            findOwnership(ownershipId),
            batteryStorageData?.batteryStorageHolds?.[currentStorageHold]?.wiOwners,
            '',
            2,
            (ownership: Ownership) => onUpdateWINote(ownership)
        );
    };

    const onSaveAddOwnership = async (values: AddWIOwnershipData) => {
        values.contractOwnership.contractLookupId = batteryStorageData.batteryStorageHolds[currentStorageHold].contractLookupId;

        await post(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/contract-ownership/${LandContractType.BatteryStorage}`,
            values
        );

        if (response.ok) {
            dispatch(
                push(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.sectionNumber}/surface-tracts/${appContext.surfaceTractNumber}/ownership/${appContext.ownershipId}/battery-storage/${appContext.batteryStorageId}/battery-storage-holds/1`
                )
            );

            getBatteryStorageData(appContext.batteryStorageId);
        } else {
            window.alert('Update Contract Ownership failed');
        }
    };

    const findOwnership = (ownershipId: number = 0) => {
        let refId = ownershipId > 0 ? ownershipId : selectedWIOwnershipId;
        let ownership = batteryStorageData.batteryStorageHolds[currentStorageHold].contractOwnerships?.find(
            (lo: WIContractOwnership) => lo.id === refId
        );

        if (!ownership) {
            batteryStorageData.batteryStorageHolds[currentStorageHold].contractOwnerships?.forEach((lo: WIContractOwnership) => {
                let tempOwnership = lo.orriOwnerships.find((wio: WIContractOwnership) => wio.id === refId);
                if (tempOwnership) {
                    ownership = tempOwnership;
                }
            });
        }

        return ownership;
    };

    // const calculateTotal = (
    //     measurementTypeId: number,
    //     paymentAmount: number,
    //     grossAcres: number,
    //     measurementTypesList: Array<IndexReferenceType>
    // ) => {
    //     let measurementType = measurementTypesList.find((mt) => mt.id === measurementTypeId)?.name;

    //     return measurementType !== undefined ? Number(MathUtil.cleanDollarAmount(measurementType === 'Per Item' ? paymentAmount * grossAcres : paymentAmount)) : 0;
    // };

    // const handleTotals = () => {
    //     let rep = getValues();
    //     let totalBonus = 0;
    //     let grossAcres = !isNaN(parseFloat(rep?.originalGrossAcres)) ? parseFloat(rep.originalGrossAcres) : 0;

    //     let bonusTypeId = parseInt(rep?.bonusTypeId, 10);
    //     let bonusAmount = parseFloat(rep?.bonusAmount);
    //     if (!isNaN(bonusTypeId) && !isNaN(bonusAmount)) {
    //         let initialBonus = calculateTotal(bonusTypeId, bonusAmount, grossAcres, measurementTypes);
    //         setValue('initialBonus', initialBonus);
    //         totalBonus += initialBonus;
    //     }

    //     let rentalTypeId = parseInt(rep?.rentalTypeId, 10);
    //     let rentAmount = parseFloat(rep?.rentalAmount);
    //     if (!isNaN(rentalTypeId) && !isNaN(rentAmount)) {
    //         let totalRent = calculateTotal(rentalTypeId, rentAmount, grossAcres, measurementTypes);
    //         setValue('rentTotal', totalRent);
    //         totalBonus += totalRent;
    //     }

    //     let flatBonus = !isNaN(parseFloat(rep?.flatBonus)) ? parseFloat(rep?.flatBonus) : 0;
    //     let additionalBonus = !isNaN(parseFloat(rep?.addlBonusAmount)) ? parseFloat(rep?.addlBonusAmount) : 0;

    //     totalBonus += flatBonus + additionalBonus;
    //     setValue('totalBonus', totalBonus);

    //     let dueDiligenceType = parseInt(rep?.currentHold?.dueDiligenceFeeTypeId, 10);
    //     let dueDiligence = parseFloat(rep?.currentHold?.dueDiligenceFee);
    //     if (!isNaN(dueDiligence) && !isNaN(dueDiligenceType)) {
    //         setValue('currentHold.dueDiligenceFeeTotal', calculateTotal(dueDiligenceType, dueDiligence, grossAcres, measurementTypes));
    //     }

    //     let dueDiligenceExtensionFeeType = parseInt(rep?.currentHold?.dueDiligenceExtensionFeeTypeId, 10);
    //     let dueDiligenceExtensionFee = parseFloat(rep?.currentHold?.dueDiligenceExtensionFee);
    //     if (!isNaN(dueDiligenceExtensionFee) && !isNaN(dueDiligenceExtensionFeeType)) {
    //         setValue(
    //             'currentHold.dueDiligenceExtensionFeeTotal',
    //             calculateTotal(dueDiligenceExtensionFeeType, dueDiligenceExtensionFee, grossAcres, measurementTypes)
    //         );
    //     }

    //     let constructionFeeType = parseInt(rep?.currentHold?.constructionFeeTypeId, 10);
    //     let constructionFee = parseFloat(rep?.currentHold?.constructionFee);
    //     if (!isNaN(constructionFee) && !isNaN(constructionFeeType)) {
    //         setValue('currentHold.constructionFeeTotal', calculateTotal(constructionFeeType, constructionFee, grossAcres, measurementTypes));
    //     }

    //     let operatingIPFFee = parseInt(rep?.currentHold?.operatingIPFFee, 10);
    //     let operatingIPFFeeType = parseFloat(rep?.currentHold?.operatingIPFFeeTypeId);
    //     if (!isNaN(operatingIPFFee) && !isNaN(operatingIPFFeeType)) {
    //         setValue('currentHold.operatingIPFFeeTotal', calculateTotal(operatingIPFFeeType, operatingIPFFee, grossAcres, measurementTypes));
    //     }

    //     let operatingOPFFee = parseInt(rep?.currentHold?.operatingOPFFee, 10);
    //     let operatingOPFFeeType = parseFloat(rep?.currentHold?.operatingOPFFeeTypeId);
    //     if (!isNaN(operatingOPFFee) && !isNaN(operatingOPFFeeType)) {
    //         setValue('currentHold.operatingOPFFeeTotal', calculateTotal(operatingOPFFeeType, operatingOPFFee, grossAcres, measurementTypes));
    //     }
    // };

    const warningStyle: React.CSSProperties = {
        display: 'inline-block',
        padding: '0.5rem',
        margin: '0 0 1rem 0',
        fontWeight: 700,
        background: '#d5d5d5',
        fontSize: '.8em',
        color: '#565656',
        width: '100%',
        textAlign: 'center',
        borderRadius: '0.4rem',
    };

    return (
        <div className="lease-info-page">
            <div>
                <form onSubmit={handleSubmit((data) => onSubmit(data as unknown as BatteryStorage))}>
                    <div>
                        {isDirty && (
                            <>
                                <div
                                    id="dirtyFlag"
                                    style={{
                                        position: 'fixed',
                                        top: '4.7rem',
                                        left: '0',
                                        width: '100%',
                                        color: 'white',
                                        backgroundColor: 'rgba(255,0,0,.5)',
                                        zIndex: 9000,
                                        padding: '1rem 0',
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <div>Unsaved Changes - Please Save Before Leaving Form</div>
                                    <div style={{ position: 'absolute', right: '.5rem', top: '50%', transform: 'translateY(-50%)', display: 'flex' }}>
                                        <div>
                                            <Button text="Save" type="submit" size="small" intent="primary" />
                                        </div>
                                        <div style={{ margin: '0 1rem', borderRight: '1px solid white', width: '1px' }} />
                                        <Button text="Cancel Changes" size="small" onClick={() => reset()} />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Added to let the user know fields with * are required */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', marginRight: '1rem' }}>
                        <span style={{ color: 'red', transform: 'scale(2)' }}>*</span>
                        <p>= Fields are required!</p>
                    </div>

                    <RHFLegend title="BESS Lease" />
                    <div hidden={true}>
                        <RHFInputBox label="" name="id" register={register} type="number" disabled={true} errors={errors} />
                        <RHFCheckbox name="isGeneratedLease" label="" disabled={true} register={register} errors={errors} />
                    </div>
                    <div className="form-row" style={{ width: '60%' }}>
                        <RHFInputBox
                            label="Contract Name"
                            name="contractName"
                            style={{ width: '100%' }}
                            register={register}
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                        />
                    </div>
                    <div className="form-row">
                        <RHFInputBox
                            label="DLC Number"
                            name="dlcLeaseNumber"
                            style={{ width: '100%' }}
                            register={register}
                            errors={errors}
                            disabled={true}
                        />
                        <RHFInputBox
                            label="Lease Date"
                            name="leaseDate"
                            register={register}
                            type="date"
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                        />

                        <RHFInputBox
                            label="Effective Date"
                            name="effectiveDate"
                            register={register}
                            type="date"
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                        />
                    </div>
                    <div className="form-row">
                        <RHFInputBox label="Volume" name="volume" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Page" name="page" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Inst. Number" name="instrumentNumber" register={register} type="text" errors={errors} />
                        <RHFInputBox
                            label="Recorded Date"
                            name="recordedDate"
                            register={register}
                            type="date"
                            errors={errors}
                        />
                        <RHFInputBox label="Fed/State Lease #" name="fedStateLeaseNumber" register={register} type="number" errors={errors} />
                        <RHFInputBox label="Recording Date" name="recordDate" register={register} type="date" errors={errors} />
                        <RHFCheckbox name="isFiledByMemo" label="Filed By Memorandum" register={register} errors={errors} />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Original Gross Acres"
                            name="originalGrossAcres"
                            style={{ width: '100%' }}
                            register={register}
                            type="number"
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                        />
                        <RHFInputBox
                            label="Original Lessor"
                            name="ownersOnDocument"
                            style={{ width: '100%' }}
                            register={register}
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                        />
                        <RHFInputBox label="Original Lessee" name="lesseeName" style={{ width: '100%' }} register={register} errors={errors} />
                        <RHFInputBox label="Lessor AKA" name="lessorAka" style={{ width: '100%' }} register={register} errors={errors} />
                        <RHFInputBox label="Client Doc Number" name="clientDocNumber" register={register} type="text" errors={errors} />
                    </div>

                    {/* TODO: Set this up for multiple Surface Waiver Holds. */}
                    <RHFLegend title="BESS Leasehold" />
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                        <div className="form-row">
                            <Button text="Previous" onClick={previousContractHold} size="small" intent="primary" />
                            {/* <span onClick={previousLeasehold} style={{ border: '1px solid black' }} className="clickable">Previous</span> */}
                            <span style={{ padding: '.5rem' }}>{`${currentStorageHold + 1} of ${
                                batteryStorageData?.batteryStorageHolds?.length || 1
                            }`}</span>
                            <Button text="Next" onClick={nextContractHold} size="small" intent="primary" />
                            {/* <span onClick={nextLeasehold} style={{ border: '1px solid black' }} className="clickable">Next</span> */}
                            {batteryStorageData?.batteryStorageHolds?.[currentStorageHold]?.surfaceOwner && (
                                <div style={{ marginLeft: '2rem', fontSize: '1.5rem' }}>
                                    <span>Owner: </span>
                                    <span>
                                        {OwnerUtil.MapOwner(
                                            batteryStorageData?.batteryStorageHolds?.[currentStorageHold]?.surfaceOwner
                                        )?.ownerName() || 'N/A'}
                                    </span>
                                </div>
                            )}
                        </div>
                        {batteryStorageData?.batteryStorageHolds?.length > 1 && (
                            <div style={{ display: 'flex' }}>
                                {copyLeaseholdClicked && (
                                    <div style={{textAlign: 'center', marginRight: '1rem'}}>
                                        <RHFSelect 
                                            onChange={(e) => handleSourceSelected(e)} 
                                            name="sourceLeaseholdId" 
                                            label="Source Leasehold" 
                                            options={leaseholdList()} 
                                            register={register} 
                                        />
                                        <Button 
                                            style={{marginTop: '1rem'}}
                                            disabled={!sourceSelected} 
                                            text="Confirm Copy"
                                            intent="danger" 
                                            onClick={onCopyLeasehold} 
                                        />
                                    </div>
                                )}
                                <div>
                                    {appContext.batteryStorageHoldId === 0 && (
                                        <Button
                                            style={{ marginLeft: '2rem' }}
                                            text="Copy Leasehold"
                                            onClick={() => setCopyLeaseholdClicked(true)}
                                            intent="primary" 
                                        />
                                    )}
                                    <Button
                                        style={{ marginLeft: '2rem' }}
                                        text="Delete Leasehold"
                                        onClick={() => deleteBatteryStorageHold(false)}
                                        intent="danger"
                                    />
                                </div>
                            </div>
                        )}
                        {batteryStorageData?.batteryStorageHolds?.length === 1 && (
                            <div style={{ display: 'flex' }}>
                                <div>
                                    <Button
                                        style={{ marginLeft: '2rem' }}
                                        text="Delete Battery Storage"
                                        onClick={() => deleteBatteryStorageHold(true)}
                                        intent="danger"
                                    />
                                </div>
                            </div>                        
                        )}
                    </div>
                    <div className="form-row">
                        <RHFCheckbox 
                            style={{marginBottom: '2rem'}}
                            label={'Active Lease'} 
                            name={'currentHold.isActive'} 
                            register={register} 
                            errors={errors} 
                        />
                    </div>
                    <div hidden={true}>
                        <RHFInputBox label="" name="currentHold.id" register={register} type="number" disabled={true} errors={errors} />
                        <RHFInputBox label="" name="currentHold.batteryStorageId" register={register} type="number" disabled={true} errors={errors} />
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                    <div className="form-row">
                            <RHFInputBox
                                label="Execution Bonus"
                                name="batteryStorageHolds[0].executionBonus"
                                register={register}
                                type="number"
                                errors={errors}
                                step={0.0001}
                            />
                            <RHFSelect
                                name="batteryStorageHolds[0].executionBonusTypeId"
                                label=""
                                options={getOptions('Fee', 'acre')}
                                register={register}
                                errors={errors}
                            />
                            <RHFInputBox
                                label="Execution Bonus Total"
                                name="batteryStorageHolds[0].executionBonusTotal"
                                register={register}
                                type="number"
                                errors={errors}
                                step={0.0001}
                                disabled={true}
                            />
                        </div>
                        <div className="form-row" style={{ marginBottom: '1rem' }}>
                            <RHFCheckbox label={'Construction Period'} name={'currentHold.constructionPeriod'} register={register} errors={errors} />
                            {watchEasementFields['currentHold.constructionPeriod'] && (
                                <>
                                    <RHFInputBox
                                        label="ConstructionFee"
                                        name="currentHold.constructionFee"
                                        register={register}
                                        type="number"
                                        errors={errors}
                                        // onChange={handleTotals}
                                    />
                                    <RHFSelect
                                        label=""
                                        name="currentHold.constructionFeeTypeId"
                                        options={getOptions('Fee', 'linear foot')}
                                        // onChange={handleTotals}
                                        register={register}
                                        errors={errors}
                                    />

                                    <RHFLegend title="Construction Details" />
                                    <div>
                                        <RHFTextArea
                                            label=""
                                            name="currentHold.constructionDetails"
                                            style={{
                                                width: '100%',
                                                height: '4rem',
                                                border: '0',
                                                borderBottom: '1px solid black',
                                                resize: 'none',
                                                overflowY: 'auto',
                                            }}
                                            register={register}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="form-row" style={{ marginBottom: '1rem' }}>
                            <RHFCheckbox label={'Construction Extension'} name={'currentHold.constructionExtensionPeriod'} register={register} errors={errors} />
                            {watchEasementFields['currentHold.constructionExtensionPeriod'] && (
                                <>
                                    <RHFInputBox
                                        label="Access Easement Fee"
                                        name="currentHold.constructionExtensionFee"
                                        register={register}
                                        type="number"
                                        errors={errors}
                                        // onChange={handleTotals}
                                    />
                                    <RHFSelect
                                        label=""
                                        name="currentHold.constructionExtensionFeeTypeId"
                                        options={getOptions('Fee', 'linear foot')}
                                        // onChange={handleTotals}
                                        register={register}
                                        errors={errors}
                                    />
                                    
                                    <RHFLegend title="Construction Extension Details" />
                                    <div>
                                        <RHFTextArea
                                            label=""
                                            name="currentHold.constructionExtensionDetails"
                                            style={{
                                                width: '100%',
                                                height: '4rem',
                                                border: '0',
                                                borderBottom: '1px solid black',
                                                resize: 'none',
                                                overflowY: 'auto',
                                            }}
                                            register={register}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="form-row" style={{ marginBottom: '1rem' }}>
                            <RHFCheckbox label={'Access Easement'} name={'currentHold.accessEasement'} register={register} errors={errors} />
                            {watchEasementFields['currentHold.accessEasement'] && (
                                <>
                                    <RHFInputBox
                                        label="Access Easement Fee"
                                        name="currentHold.accessFee"
                                        register={register}
                                        type="number"
                                        errors={errors}
                                        // onChange={handleTotals}
                                    />
                                    <RHFSelect
                                        label=""
                                        name="currentHold.accessFeeTypeId"
                                        options={getOptions('Fee', 'linear foot')}
                                        // onChange={handleTotals}
                                        register={register}
                                        errors={errors}
                                    />
                                    <RHFInputBox
                                        label="Access Easement Width"
                                        name="currentHold.AccessWidth"
                                        register={register}
                                        type="number"
                                        errors={errors}
                                        // onChange={handleTotals}
                                    />
                                    <RHFSelect
                                        label=""
                                        name="currentHold.accessWidthTypeId"
                                        options={getOptions('Fee', 'linear foot')}
                                        // onChange={handleTotals}
                                        register={register}
                                        errors={errors}
                                    />
                                </>
                            )}
                        </div>

                        <RHFLegend title="Access Easement Details" />
                        <div>
                            <RHFTextArea
                                label=""
                                name="currentHold.accessDetails"
                                style={{
                                    width: '100%',
                                    height: '4rem',
                                    border: '0',
                                    borderBottom: '1px solid black',
                                    resize: 'none',
                                    overflowY: 'auto',
                                }}
                                register={register}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <div className="form-row" style={{ marginBottom: '1rem' }}>
                            <RHFCheckbox
                                label={'Transmission Easement'}
                                name={'currentHold.transmissionEasement'}
                                register={register}
                                errors={errors}
                            />
                            {watchEasementFields['currentHold.transmissionEasement'] && (
                                <>
                                    <RHFInputBox
                                        label="Transmission Easement Fee"
                                        name="currentHold.transmissionFee"
                                        register={register}
                                        type="number"
                                        errors={errors}
                                        // onChange={handleTotals}
                                    />
                                    <RHFSelect
                                        label=""
                                        name="currentHold.transmissionFeeTypeId"
                                        options={getOptions('Fee', 'linear foot')}
                                        // onChange={handleTotals}
                                        register={register}
                                        errors={errors}
                                    />
                                    <RHFInputBox
                                        label="Transmission Easement Width"
                                        name="currentHold.transmissionWidth"
                                        register={register}
                                        type="number"
                                        errors={errors}
                                        // onChange={handleTotals}
                                    />
                                    <RHFSelect
                                        label=""
                                        name="currentHold.transmissionWidthTypeId"
                                        options={getOptions('Fee', 'linear foot')}
                                        // onChange={handleTotals}
                                        register={register}
                                        errors={errors}
                                    />
                                </>
                            )}
                        </div>

                        <RHFLegend title="Transmission Easement Details" />
                        <div>
                            <RHFTextArea
                                label={''}
                                name="currentHold.transmissionDetails"
                                style={{
                                    width: '100%',
                                    height: '4rem',
                                    border: '0',
                                    borderBottom: '1px solid black',
                                    resize: 'none',
                                    overflowY: 'auto',
                                }}
                                register={register}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <div className="form-row" style={{ marginBottom: '1rem' }}>
                            <RHFCheckbox label={'Temporary Use Area(s)'} name={'currentHold.temporaryUseAreas'} register={register} errors={errors} />
                            {watchEasementFields['currentHold.temporaryUseAreas'] && (
                                <>
                                    <RHFInputBox
                                        label="Temporary Use Area(s) Fee"
                                        name="currentHold.temporaryUseFee"
                                        register={register}
                                        type="number"
                                        errors={errors}
                                        // onChange={handleTotals}
                                    />
                                    <RHFSelect
                                        label=""
                                        name="currentHold.temporaryUseFeeTypeId"
                                        options={getOptions('Fee', 'linear foot')}
                                        // onChange={handleTotals}
                                        register={register}
                                        errors={errors}
                                    />
                                </>
                            )}
                        </div>

                        <RHFLegend title="Temporary Use Area(s) Details" />
                        <div>
                            <RHFTextArea
                                label={''}
                                name="currentHold.temporaryUseDetails"
                                style={{
                                    width: '100%',
                                    height: '4rem',
                                    border: '0',
                                    borderBottom: '1px solid black',
                                    resize: 'none',
                                    overflowY: 'auto',
                                }}
                                register={register}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <div className="form-row" style={{ marginBottom: '1rem' }}>
                            <RHFCheckbox label={'Crop Buyout'} name={'currentHold.cropBuyout'} register={register} errors={errors} />
                            {watchEasementFields['currentHold.cropBuyout'] && (
                                <>
                                    <RHFInputBox
                                        label="Crop Buyout Fee"
                                        name="currentHold.cropBuyoutFee"
                                        register={register}
                                        type="number"
                                        errors={errors}
                                        // onChange={handleTotals}
                                    />
                                    <RHFSelect
                                        label=""
                                        name="currentHold.cropBuyoutFeeTypeId"
                                        options={getOptions('Fee', 'linear foot')}
                                        // onChange={handleTotals}
                                        register={register}
                                        errors={errors}
                                    />
                                </>
                            )}
                        </div>

                        <RHFLegend title="Crop Buyout Terms and Details" />
                        <div>
                            <RHFTextArea
                                label={''}
                                name="currentHold.cropBuyoutDetails"
                                style={{
                                    width: '100%',
                                    height: '4rem',
                                    border: '0',
                                    borderBottom: '1px solid black',
                                    resize: 'none',
                                    overflowY: 'auto',
                                }}
                                register={register}
                            />
                        </div>
                    </div>

                    <div style={{ margin: '2rem auto', marginLeft: '7rem' }} className="form-row">
                        <RHFCheckbox label="Development Period?" name="currentHold.developmentPeriod" register={register} errors={errors} />
                        {watchPeriodFields['currentHold.developmentPeriod'] && (
                            <>
                                <RHFInputBox
                                    label="Development Period Term"
                                    name="currentHold.developmentPeriodTerm"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                />
                                <RHFSelect
                                    label=""
                                    name="currentHold.developmentPeriodTermTypeId"
                                    options={getOptions('Time')}
                                    register={register}
                                    errors={errors}
                                />
                                <RHFInputBox
                                    label="Development Rent Amount"
                                    name="currentHold.developmentRentAmount"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                    // onChange={handleTotals}
                                />
                                <RHFSelect
                                    label=""
                                    name="currentHold.developmentRentTermTypeId"
                                    options={getOptions('Fee', 'acre')}
                                    // onChange={handleTotals}
                                    register={register}
                                    errors={errors}
                                />
                            </>
                        )}
                    </div>

                    <div style={{ margin: '2rem auto', marginLeft: '7rem' }} className="form-row">
                        <RHFCheckbox
                            label="Development Period Extension?"
                            name="currentHold.developmentPeriodExtension"
                            register={register}
                            errors={errors}
                        />

                        {watchPeriodFields['currentHold.developmentPeriodExtension'] && (
                            <>
                                <RHFInputBox
                                    label="Development Period Extension Term"
                                    name="currentHold.developmentPeriodExtensionTerm"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                />
                                <RHFSelect
                                    label=""
                                    name="currentHold.developmentPeriodExtensionTermTypeId"
                                    options={getOptions('Time')}
                                    register={register}
                                    errors={errors}
                                />
                                <RHFInputBox
                                    label="Extension Rent Amount"
                                    name="currentHold.firstExtensionOptionRentAmount"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                    // onChange={handleTotals}
                                />
                                <RHFSelect
                                    label=""
                                    name="currentHold.firstExtensionOptionRentTermTypeId"
                                    options={getOptions('LandFee')}
                                    // onChange={handleTotals}
                                    register={register}
                                    errors={errors}
                                />
                            </>
                        )}
                    </div>

                    <div style={{ margin: '2rem auto', marginLeft: '7rem' }} className="form-row">
                        <RHFCheckbox
                            label="Development Period Second Extension?"
                            name="currentHold.developmentPeriodSecondExtension"
                            register={register}
                            errors={errors}
                        />
                        {watchPeriodFields['currentHold.developmentPeriodSecondExtension'] && (
                            <>
                                <RHFInputBox
                                    label="Second Extension Term"
                                    name="currentHold.developmentPeriodSecondExtensionTerm"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                />
                                <RHFSelect
                                    label=""
                                    name="currentHold.developmentPeriodSecondExtensionTermTypeId"
                                    options={getOptions('Time')}
                                    register={register}
                                    errors={errors}
                                />
                                <RHFInputBox
                                    label="Second Extension Rent Amount"
                                    name="currentHold.secondExtensionOptionTerm"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                    // onChange={handleTotals}
                                />
                                <RHFSelect
                                    label=""
                                    name="currentHold.secondExtensionOptionTermTypeId"
                                    options={getOptions('LandFee')}
                                    // onChange={handleTotals}
                                    register={register}
                                    errors={errors}
                                />
                            </>
                        )}
                    </div>

                    <div style={{ margin: '2rem auto', marginLeft: '7rem' }}>
                        <RHFCheckbox label="Operation Period?" name="currentHold.operationsPeriod" register={register} errors={errors} />
                        {watchPeriodFields['currentHold.operationsPeriod'] && (
                            <>
                                <div className="form-row">
                                    <RHFInputBox
                                        label="Operation Period Term"
                                        name="currentHold.operationsPeriodTerm"
                                        register={register}
                                        type="number"
                                        errors={errors}
                                    />
                                    <RHFSelect
                                        label=""
                                        name="currentHold.operationsPeriodTermTypeId"
                                        options={getOptions('Time')}
                                        register={register}
                                        errors={errors}
                                    />
                                    <RHFInputBox
                                        label="Operations Base Rent Amount"
                                        name="currentHold.operationsBaseRentAmount"
                                        register={register}
                                        type="number"
                                        errors={errors}
                                        // onChange={handleTotals}
                                    />
                                    <RHFSelect
                                        label=""
                                        name="currentHold.operationsBaseRentTermTypeId"
                                        options={getOptions('LandFee')}
                                        // onChange={handleTotals}
                                        register={register}
                                        errors={errors}
                                    />
                                </div>

                                <div style={{ margin: '2rem auto' }} className="form-row">
                                    <RHFInputBox
                                        label="Annual Escalation Rate (Use decimal format)"
                                        name="currentHold.annualEscalationRate"
                                        register={register}
                                        type="number"
                                        errors={errors}
                                    />
                                    <RHFCheckbox
                                        label={'Above Prime Rate?'}
                                        name={'currentHold.escalationAbovePrime'}
                                        register={register}
                                        errors={errors}
                                    />
                                </div>

                                <div style={{ margin: '2rem auto' }} className="form-row">
                                    <RHFInputBox
                                        label="Annual Overdue Rate (Use decimal format)"
                                        name="currentHold.overdueRateTerm"
                                        register={register}
                                        type="number"
                                        errors={errors}
                                    />
                                    <RHFCheckbox
                                        label={'Above Prime Rate?'}
                                        name={'currentHold.overdueAbovePrime'}
                                        register={register}
                                        errors={errors}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <div style={{ margin: '2rem auto', marginLeft: '7rem' }} className="form-row">
                        <RHFCheckbox
                            label="Extended Operation Period?"
                            name="currentHold.extendedOperationsPeriod"
                            register={register}
                            errors={errors}
                        />
                        {watchPeriodFields['currentHold.extendedOperationsPeriod'] && (
                            <>
                                <RHFInputBox
                                    label="Extended Operation Term"
                                    name="currentHold.extendedOperationsPeriodTerm"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                />
                                <RHFSelect
                                    label=""
                                    name="currentHold.extendedOperationsPeriodTermTypeId"
                                    options={getOptions('Time')}
                                    register={register}
                                    errors={errors}
                                />
                                <RHFInputBox
                                    label="Extended Operations Rent Amount"
                                    name="currentHold.extendedOperationsRentAmount"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                    // onChange={handleTotals}
                                />
                                <RHFSelect
                                    label=""
                                    name="currentHold.extendedOperationsRentTermTypeId"
                                    options={getOptions('LandFee')}
                                    // onChange={handleTotals}
                                    register={register}
                                    errors={errors}
                                />
                            </>
                        )}
                    </div>

                    <div style={{ margin: '2rem auto', marginLeft: '7rem' }} className="form-row">
                        <RHFCheckbox label="Restoration Period?" name="currentHold.restorationPeriod" register={register} errors={errors} />
                        {watchPeriodFields['currentHold.restorationPeriod'] && (
                            <>
                                <RHFInputBox
                                    label="Restoration Term"
                                    name="currentHold.restorationTerm"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                />
                                <RHFSelect
                                    label=""
                                    name="currentHold.restorationTermTypeId"
                                    options={getOptions('Time')}
                                    register={register}
                                    errors={errors}
                                />
                                <RHFInputBox
                                    label="Restoration Rent Amount"
                                    name="currentHold.restorationTermRentAmount"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                    // onChange={handleTotals}
                                />
                                <RHFSelect
                                    label=""
                                    name="currentHold.restorationTermRentTypeId"
                                    options={getOptions('LandFee')}
                                    // onChange={handleTotals}
                                    register={register}
                                    errors={errors}
                                />
                            </>
                        )}
                    </div>

                    <div style={{ margin: '2rem auto', marginLeft: '7rem' }} className="form-row">
                        <RHFCheckbox label="Substation Area?" name="currentHold.substationArea" register={register} errors={errors} />
                        {watchEasementFields['currentHold.substationArea'] && (
                            <>
                                <RHFInputBox
                                    label="Restoration Term"
                                    name="currentHold.substationFee"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                />
                                <RHFSelect
                                    label=""
                                    name="currentHold.substationFeeTypeId"
                                    options={getOptions('Time')}
                                    register={register}
                                    errors={errors}
                                />

                                <RHFLegend title="Substation Area Details" />
                                <div>
                                    <RHFTextArea
                                        label={''}
                                        name="currentHold.substationAreaDetails"
                                        style={{
                                            width: '100%',
                                            height: '4rem',
                                            border: '0',
                                            borderBottom: '1px solid black',
                                            resize: 'none',
                                            overflowY: 'auto',
                                        }}
                                        register={register}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <div style={{ margin: '2rem auto', marginLeft: '7rem' }}>
                        <RHFCheckbox label={'Rights to Assign?'} name={'currentHold.rightsToAssign'} register={register} errors={errors} />
                    </div>

                    <RHFLegend title="Original Legal Description" />
                    <div className="form-row">
                        <RHFTextArea
                            label={''}
                            name={'legalDescription'}
                            style={{
                                width: '100%',
                                height: '6rem',
                                border: '0',
                                borderBottom: '1px solid black',
                                resize: 'none',
                                overflowY: 'auto',
                            }}
                            register={register}
                        />
                    </div>

                    <RHFLegend title="Contract Notes" />
                    <div className="form-row" style={{ width: '100%' }}>
                        <RHFTextArea
                            label={''}
                            name={'leaseLessorNotes'}
                            style={{
                                width: '100%',
                                height: '6rem',
                                border: '0',
                                borderBottom: '1px solid black',
                                resize: 'none',
                                overflowY: 'auto',
                            }}
                            register={register}
                        />
                    </div>
                    
                    <RHFLegend title="Pooling Information" />

                    <div className="form-row">
                        <RHFToggle label="HBP" name="currentHold.hbp" register={register} errors={errors} />
                        <RHFInputBox
                            label="Pooling Order Number"
                            name="currentHold.poolingOrderNumber"
                            register={register}
                            type="text"
                            errors={errors}
                        />
                        <RHFInputBox label="Operator" name="currentHold.operator" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Well Name" name="currentHold.wellName" register={register} type="text" errors={errors} />
                        <RHFToggle label="HBU" name="currentHold.hbu" register={register} errors={errors} />
                        <RHFInputBox label="API #" name="currentHold.apiNumber" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Unit Name" name="currentHold.unitName" register={register} type="text" errors={errors} />
                        <RHFInputBox label="Field Name" name="currentHold.fieldName" register={register} type="text" errors={errors} />
                    </div>

                    <RHFLegend title="Provisions" />
                    {attachedProvisions && (
                        <div style={{ margin: 'auto 2rem' }}>
                            <ProvisionClause
                                attachedProvisionClauses={attachedProvisions}
                                options={provisionClauseTypes}
                                formPrefix="currentHold.provisionLeaseClauses"
                                register={register}
                                errors={errors}
                                getValues={getValues}
                                setValue={setValue}
                                key="provisionClause"
                            />
                        </div>
                    )}

                    <RHFLegend title="Document Management" />
                    {batteryStorageData?.batteryStorageHolds && (
                        <ContractHoldDocs
                            showDialog={(dialogName: string) => dispatch(actions.layout.showDialog(dialogName))}
                            hideDialog={() => dispatch(actions.layout.hideDialog())}
                            contractLookupId={batteryStorageData?.batteryStorageHolds?.[currentStorageHold]?.contractLookupId}
                            contractType={'Contract Hold'}
                        />
                    )}

                    <RHFLegend title="Contract Ownership" />
                    <span style={warningStyle}>ALL INTEREST REPRESENTED AS DECIMAL</span>
                    <div style={{ minHeight: '20rem' }}>
                        <ContractWorkingInterestList
                            isGovStateBlm={false}
                            owners={batteryStorageData?.batteryStorageHolds?.[currentStorageHold]?.wiOwners}
                            wiContractOwnerships={batteryStorageData?.batteryStorageHolds?.[currentStorageHold]?.contractOwnerships}
                            prefix={'currentHold'}
                            setDialogShowing={(dialogName: string) => dispatch(actions.layout.showDialog(dialogName))}
                            tryAddWI={onTryAddWI}
                            onAddORRI={onAddORRI}
                            onCopyORRI={onCopyORRI}
                            onDeleteORRI={onDeleteORRI}
                            deleteContractOwnership={onDeleteContractOwnership}
                            openOwnershipNotes={openOwnershipNotes}
                            register={register}
                            errors={errors}
                            getValues={getValues}
                            setValue={setValue}
                            watch={watch}
                            royalty={royalty}
                        />
                    </div>

                    <div>
                        <Button text="Save" type="submit" intent="primary" />
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BatteryStorageInfo;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/LeaseholdInfoComponents/BatteryStorageInfo.tsx